import { createSlice } from "@reduxjs/toolkit"
import _ from "lodash"
import { ScheduleSchemaState } from "./ScheduleSchema"
import { validateStateInDev } from "./validateStateInDev"
import {
	deallocatePatient as deallocatePatientAtomic,
	transferPatientsBetweenNursesAndChairs as transferPatientsBetweenNursesAndChairsAtomic,
	markNurseAsEdited as markNurseAsEditedAtomic,
	markPatientAsEdited as markPatientAsEditedAtomic,
	assignPatient as assignPatientAtomic,
	setPatientsCancellationConsideredForReassignment as setPatientsCancellationConsideredForReassignmentAtomic,
	setShownCancelledPatients as setShownCancelledPatientsAtomic,
	resetAllEdits as resetAllEditsAtomic
} from "./atomicActions"

const scheduleSlice = createSlice({
	name: "schedule",
	initialState: {
		isLocked: false,
		triedToLock: false,
		acuityMix: {},
		chairSchedule: [],
		nurseSchedule: [],
		// Last known server-side nurse/chair arrays. Used by useSchedulePatch
		// to diff and send only changed entries. Populated from server payload
		// in setOptimizeData, refreshed to current state on save success.
		nurseScheduleBaseline: [],
		chairScheduleBaseline: [],
		utilizationCurve: [],
		chargeNurse: null,
		patientSchedule: [],
		editedPatients: [],
		removedPatients: [],
		editedNurses: [],
		sftpReassignment: [],
		isEditing: false,
		openTime: "08:00",
		closeTime: "20:00",
		status: "idle", // Track loading states: 'idle', 'loading', 'succeeded', 'failed'
		error: null, // Store error messages if any
		documentId: null // Add _id to the state
	},

	/*
    Invariants:

    # Infusion Center Data Structure Invariants

    ## Time-Related Invariants

    1. All time fields are represented as minutes since midnight (0-1440)
      - Valid times must be non-negative integers
      - Times cannot exceed 1440 (24 hours * 60 minutes)
      - Examples: 480 = 8:00 AM, 960 = 4:00 PM

    2. Time Sequence Requirements
      - scheduledStartTime must be before scheduledEndTime
      - actualStartTime must be before actualEndTime (when both exist)
      - arrivalTime should be before actualStartTime (when both exist)
      - scheduledEndTime must be before or equal to dueTime

    ## Patient-Related Invariants

    1. Acuity Levels
      - Must be an integer between 1 and 5
      - Each patient must have exactly one acuity level assigned

    2. Visit Status
      - Must be one of: "Completed", "Scheduled", "Checked In", "Cancellation", "Add on", "Ongoing", "No Show"

    ## Staff-Related Invariants

    1. Nurse Roles
      - Must be one of: "RN", "LVN", or "Charge"
      - Each nurse must have exactly one role assigned
      - There must be exactly one Charge nurse per schedule

    ## Resource-Related Invariants

    1. Chair Types
      - Each chair location must be exactly one of: Bed, Chair, or Gurney
      - This is represented by three boolean flags where exactly one must be true
      - Each chair must have a unique chairId and chairName

    ## Assignment Integrity Invariants

    1. Patient Assignment Requirements
      - If a patient has an assignment, both assignedNurse and assignedChair must be non-null
      - patient.assignedNurse / patient.assignedChair are the source of truth for assignments
      - Use getNursePatients / getChairPatients from derivedAssignments.js to derive the inverse

    ## Summary Statistics

    1. Acuity Mix
      - Must maintain accurate counts of patients by acuity level
      - Total of acuity mix numbers should match total number of patients
      - Values must be non-negative integers
  */

	reducers: {
		// NEW REDUCERS ENFORCE THE INVARIANTS

		deallocatePatient: deallocatePatientAtomic(ScheduleSchemaState),

		transferPatientsBetweenNursesAndChairs:
			transferPatientsBetweenNursesAndChairsAtomic(ScheduleSchemaState),

		/**
		 * Mark a nurse as edited
		 * @param {Object} state - The current state of the schedule
		 * @param {Object} action - The action object containing the nurseId
		 * @returns {Object} The updated state with the nurse marked as edited
		 */
		markNurseAsEdited: markNurseAsEditedAtomic(ScheduleSchemaState),

		/**
		 * Mark a patient as edited
		 * @param {Object} state - The current state of the schedule
		 * @param {Object} action - The action object containing the patientId and editedProperties
		 * @returns {Object} The updated state with the patient marked as edited
		 */
		markPatientAsEdited: markPatientAsEditedAtomic(ScheduleSchemaState),

		assignPatient: assignPatientAtomic(ScheduleSchemaState),

		/**
		 * Set the patients considered for cancellation reassignments
		 * @param {Object} state - The current state of the schedule
		 * @param {{payload: {patientIds: number[]}}} action - The action object containing the patientIds
		 * @returns {Object} The updated state with the patients considered for cancellation reassignments
		 */
		setPatientsCancellationConsideredForReassignment:
			setPatientsCancellationConsideredForReassignmentAtomic(ScheduleSchemaState),

		/**
		 * Set the patients marked as shown cancelled patients
		 * @param {Object} state - The current state of the schedule
		 * @param {{payload: {patientIds: number[]}}} action - The action object containing the patientIds
		 * @returns {Object} The updated state with the patients marked as shown cancelled
		 */
		setShownCancelledPatients:
			setShownCancelledPatientsAtomic(ScheduleSchemaState),

		resetAllEdits: resetAllEditsAtomic(ScheduleSchemaState),

		// OLD REDUCERS DON'T ENFORCE THE INVARIANTS

		setOptimizeLoading: state => {
			state.status = "loading"
			state.error = null // Clear any previous error
		},
		setOptimizeData: (state, action) => {
			const newState = _.cloneDeep(state)

			newState.status = "succeeded"
			const payload = action.payload
			newState.isLocked = payload.locked
			newState.acuityMix = payload.acuityMix
			newState.chairSchedule = payload.chairSchedule
			newState.nurseSchedule = payload.nurseSchedule ?
				[
					...newState.nurseSchedule.map(existingNurse =>
						payload.nurseSchedule.find(nurse => nurse.nurseId === existingNurse.nurseId) || existingNurse
					),
					...payload.nurseSchedule.filter(nurse =>
						!newState.nurseSchedule.some(existingNurse => existingNurse.nurseId === nurse.nurseId)
					)
				] : newState.nurseSchedule;
			// Baselines reflect pure server truth (no local-edit merge applied).
			newState.nurseScheduleBaseline = payload.nurseSchedule || newState.nurseScheduleBaseline
			newState.chairScheduleBaseline = payload.chairSchedule || newState.chairScheduleBaseline
			newState.utilizationCurve = payload.utilizationCurve
			newState.patientSchedule = payload.patientSchedule.filter(patient => {
				return !patient.dateOfRemoval || patient.dateOfRemoval === payload.date;
			})
			newState.chargeNurse = payload.chargeNurse
			newState.openTime = payload.openTime
			newState.closeTime = payload.closeTime
			newState.documentId = payload._id
			newState.error = null // Clear any previous error on success

			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setOptimizeError: (state, action) => {
			state.status = "failed"
			state.error = action.payload // Store the error message
		},
		// After a successful save, the current nurse/chair arrays ARE the new
		// server-side truth — snapshot them so the next save's diff is fresh.
		setBaselineToCurrent: state => {
			state.nurseScheduleBaseline = _.cloneDeep(state.nurseSchedule)
			state.chairScheduleBaseline = _.cloneDeep(state.chairSchedule)
		},
		updateSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { patientSchedule, nurseSchedule, chairSchedule } = action.payload

			const newState = _.cloneDeep(state)

			if (patientSchedule) {
				newState.patientSchedule = patientSchedule
			}
			if (nurseSchedule) {
				newState.nurseSchedule = nurseSchedule
			}
			if (chairSchedule) {
				newState.chairSchedule = chairSchedule
			}

			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setLockStatus: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.isLocked = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setTriedToLock: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.triedToLock = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setAcuityMix: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.acuityMix = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setChairSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.chairSchedule = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setNurseSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.nurseSchedule = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setUtilizationCurve: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.utilizationCurve = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		removeNurse: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const nurseId = action.payload
			const editedNurseIndex = state.editedNurses.findIndex(
				nurse => nurse.nurseId === nurseId
			)

			const newState = _.cloneDeep(state)
			newState.editedNurses[editedNurseIndex].deleted = true
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		addNurse: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newNurse = action.payload
			const newState = _.cloneDeep(state)
			newState.editedNurses.push({
				nurseId: newNurse.nurseId,
				added: true,
				deleted: false,
				name: false,
				scheduledStartTime: false,
				scheduledEndTime: false,
				scheduledLunchStartTime: false,
				scheduledLunchEndTime: false
			})
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		updateEditedNurses: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { nurseId, fieldName } = action.payload
			const newState = _.cloneDeep(state)
			newState.editedNurses = newState.editedNurses.map(nurse => {
				if (nurse.nurseId === nurseId) {
					return {
						...nurse,
						[fieldName]: true // Set the specific field to true
					}
				}
				return nurse
			})
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		updateNurse: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const updatedNurse = action.payload
			const newState = _.cloneDeep(state)
			newState.nurseSchedule = newState.nurseSchedule.map(nurse =>
				nurse.nurseId === updatedNurse.nurseId ? updatedNurse : nurse
			)
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		resetPatientEdits: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const { patientId } = action.payload

			const newState = _.cloneDeep(state)
			newState.editedPatients = newState.editedPatients.map(patient => {
				if (patient.patientId === patientId) {
					return {
						...patient,
						acuity: false,
						assignedNurse: false,
						assignedChair: false,
						pendingChanges: false,
						notes: false,
						cancellationConsideredForReassignment: false,
						shownCancelledPatient: false
					}
				}
				return patient
			})
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		updatePatientField: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const { patientId, fieldName } = action.payload

			const newState = _.cloneDeep(state)
			newState.editedPatients = newState.editedPatients.map(patient => {
				if (patient.patientId === patientId) {
					return {
						...patient,
						[fieldName]: true
					}
				}
				return patient
			})
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		addPatientField: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const { patientId, fieldName } = action.payload

			// Check if patient already exists in editedPatients
			const existingPatient = state.editedPatients.find(p => p.patientId === patientId)

			const newState = _.cloneDeep(state)

			if (!existingPatient) {
				// Add new patient if it doesn't exist
				newState.editedPatients.push({
					patientId,
					assignedNurse: fieldName === "assignedNurse",
					assignedChair: fieldName === "assignedChair",
					acuity: fieldName === "acuity",
					pendingChanges: fieldName === "pendingChanges",
					notes: fieldName === "notes",
					cancellationConsideredForReassignment:
						fieldName === "cancellationConsideredForReassignment",
					shownCancelledPatient: fieldName === "shownCancelledPatient"
				})
			} else {
				// Update existing patient
				newState.editedPatients = newState.editedPatients.map(patient => {
					if (patient.patientId === patientId) {
						return {
							...patient,
							[fieldName]: true
						}
					}
					return patient
				})
			}

			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		removePatientField: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const { patientId } = action.payload

			// Remove the patient entirely from editedPatients
			const newState = _.cloneDeep(state)
			newState.editedPatients = newState.editedPatients.filter(
				patient => patient.patientId !== patientId
			)
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		updatePatientInSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const { updatedPatient } = action.payload

			// Update patientSchedule
			const newState = _.cloneDeep(state)
			newState.patientSchedule = newState.patientSchedule.map(patient =>
				patient.patientId === updatedPatient.patientId ? updatedPatient : patient
			)
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		addPatientToEditedPatients: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const newPatient = action.payload
			// Ensure patient has the same structure as adjustPatientSchedule output
			const patientEntry = {
				patientId: newPatient.patientId,
				acuity: false,
				assignedNurse: false,
				assignedChair: false,
				pendingChanges: false,
				notes: false,
				cancellationConsideredForReassignment: false,
				shownCancelledPatient: false
			}
			const newState = _.cloneDeep(state)
			newState.editedPatients.push(patientEntry)
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		removePatientFromEditedPatients: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const patientId = action.payload
			const newState = _.cloneDeep(state)
			newState.editedPatients = newState.editedPatients.filter(p => p.patientId !== patientId)
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setSftpReassignment: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const newState = _.cloneDeep(state)
			newState.sftpReassignment = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setIsEditing: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const newState = _.cloneDeep(state)
			newState.isEditing = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		setDocumentId: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const newState = _.cloneDeep(state)
			newState.documentId = action.payload
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		},
		addRemovedPatient: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}
			const newState = _.cloneDeep(state)
			newState.removedPatients.push(action.payload)
			const validatedNewState = validateStateInDev(ScheduleSchemaState, newState)
			return validatedNewState
		}
	}
})

export const {
	deallocatePatient,
	transferPatientsBetweenNursesAndChairs,
	markNurseAsEdited,
	markPatientAsEdited,
	assignPatient,
	setPatientsCancellationConsideredForReassignment,
	setShownCancelledPatients,
	setOptimizeLoading,
	setOptimizeData,
	resetAllEdits,
	setOptimizeError,
	setBaselineToCurrent,
	updateSchedule,
	setLockStatus,
	setTriedToLock,
	setAcuityMix,
	setChairSchedule,
	setNurseSchedule,
	setUtilizationCurve,
	addNurse,
	removeNurse,
	resetPatientEdits,
	updateEditedNurses,
	updateNurse,
	updatePatientField,
	addPatientField,
	removePatientField,
	updatePatientInSchedule,
	addPatientToEditedPatients,
	removePatientFromEditedPatients,
	setSftpReassignment,
	setIsEditing,
	setDocumentId,
	addRemovedPatient
} = scheduleSlice.actions

export default scheduleSlice.reducer
